
import React from "react";
import * as Icon from "react-feather";

// Permission map from localStorage
const permissionMap = JSON.parse(localStorage.getItem("permissionsMap") || "{}");
const loginUser = JSON.parse(localStorage.getItem("permissions") || "{}");

// ------------------ HELPERS ------------------
const canView = (permissionMap, screenName) =>
  permissionMap?.[screenName]?.includes("View");

const buildItems = (items) => items.filter(Boolean);

// HR screens: by screen permission, and (like single links above) while the screen is not yet registered in Auth
const hrView = (screenName) => !permissionMap[screenName] || canView(permissionMap, screenName);

// Employee screens (previously under Workshop): viewable by Employee or Workshop permission, or if not yet in Auth
const employeeView = (screenName = "Employee") =>
  canView(permissionMap, screenName) || canView(permissionMap, "Workshop") || (!permissionMap[screenName] && !permissionMap["Workshop"]);

const createMenu = ({ label, icon, items, link }) => {
  if (link) {
    // single link, not parent
    return canView(permissionMap, label) || !permissionMap[label]
      ? { label, icon, link, submenu: false, showSubRoute: false }
      : null;
  }

  const visibleItems = buildItems(items);
  if (!visibleItems.length) return null;

  return { label, icon, submenu: true, showSubRoute: false, submenuItems: visibleItems, };
};


// ------------------ MENU CONFIG ------------------
// Dealer menu (unchanged). The distributor gets its own menu below.
const DealerSidebarData = [
  {
    submenuHdr: "Main",
    submenuOpen: true,
    submenuItems: buildItems([
      // Dashboard
      createMenu({
        label: "Dashboard",
        icon: <Icon.Grid />,
        items: buildItems([

          // First dealer dashboard = home ("/"), so it's the active one after login
          loginUser.dealerId && {
            label: "Pre-Sales Dashboard",
            link: "/",
          },

          loginUser.dealerId && {
            label: "Service Dashboard",
            link: "/service-dashboard",
          },
          loginUser.dealerId && {
            label: "Parts Dashboard",
            link: "/parts-dashboard",
          },
          !loginUser.dealerId && {
            label: "Distributor Dashboard",
            link: "/distributor-dashboard",
          },
          hrView("HR Dashboard") && {
            label: "HR Dashboard",
            link: "/hr-dashboard",
          },
        ]),
      }),
      //   // ------------------ CRM ------------------
      createMenu({
        label: "CRM",
        icon: <Icon.Trello />,
        items: buildItems([
          canView(permissionMap, "CRM Inquiry") && { label: "Inquiry", link: "/CrmInquiry" },
          canView(permissionMap, "CRM Complain") && { label: "Complain", link: "/CrmComplaint" },
          canView(permissionMap, "CRM Complain") && { label: "Complaint Setup", link: "/CrmComplaintSetup" },
          canView(permissionMap, "Appointment Due List") && { label: "Service Due List", link: "/mrs-reminders", },
          createMenu({
            label: "Appointments",
            items: buildItems([
              canView(permissionMap, "Add Appointment") && { label: "New Appointment", link: "/add-appointment" },
              canView(permissionMap, "Confirm Appointments") && { label: "Confirm Appointments", link: "/crm-appointments" },
              { label: "Appointment Inquiries", link: "/AppointmentReconfirmation" },
            ]),
          }),
        ]),
      }),
      createMenu({
        label: "Pre Sales",
        icon: <Icon.Clipboard />,
        items: buildItems([
          canView(permissionMap, "Customer Inquiry") && { label: "Customer Inquiry", link: "/visiting", },
          canView(permissionMap, "Prospect") && { label: "Prospect", link: "/lead", },
          // canView(permissionMap, "Qualified Prospect") && { label: "Qualified Prospect", link: "/opportunity", },
          canView(permissionMap, "Test Drive") && { label: "Test Drive", link: "/test-drive", },
          canView(permissionMap, "Quotation") && { label: "Quotation", link: "/quotation", },
        ]),
      }),
      createMenu({
        label: "Sales",
        icon: <Icon.FileText />,
        items: buildItems([
          canView(permissionMap, "Add Sales") && { label: "New Sales", link: "/sales-add" },
          canView(permissionMap, "Order List") && { label: "Order List", link: "/sales-inquiries" },
          canView(permissionMap, "Payments") && { label: "Payments", link: "/payments" },
          { label: "Deposit Slips", link: "/deposit-slips" },
          canView(permissionMap, "Cancel/Change Sale Request") && {
            label: "Cancel / Modify Sale",
            link: "/cancleandchangesaleRequest",
          },
          canView(permissionMap, "Cancelled Sale") && { label: "Cancelled Sale", link: "/Cancelled-Sales" },
        ]),
      }),

      //   // ------------------ After Sales ------------------
      createMenu({
        label: "After Sales",
        icon: <Icon.ShoppingBag />,
        items: buildItems([
          createMenu({
            label: "Workshop",
            items: buildItems([
              createMenu({
                label: "Estimation ",
                items: buildItems([
                  canView(permissionMap, "Add Estimation") && { label: "New Estimation/Jobcard", link: "/JobCard" },
                  canView(permissionMap, "Estimation List") && { label: "Estimations List", link: "/job-card-estimations" },
                ]),
              }),
              createMenu({
                label: "Job Card",
                items: buildItems([
                  canView(permissionMap, "Workshop Load") && { label: "Workshop Load", link: "/workshop-view", },
                  canView(permissionMap, "Open Inspection Job Card") && { label: "Open Inspection", link: "/job-card-inspections", },
                  canView(permissionMap, "Open Job Card") && { label: "Open Job Card", link: "/job-card-inquiries" },
                  canView(permissionMap, "Post Service Follow-Up") && { label: "Post Service Follow-Up", link: "/PostServiceFollowUp", },
                  canView(permissionMap, "PDS Requests") && { label: "PDS Requests", link: "/pds-requests" },
                  { label: "Job Card Payments", link: "/job-card-payments" },
                ]),
              }),]),
          }),

          createMenu({
            label: "Parts",
            icon: <Icon.Archive />,
            items: buildItems([
              createMenu({
                label: "Parts Purchase",
                items: buildItems([
                  canView(permissionMap, "Add Purchase Order") && { label: "Parts Demand", link: "/parts-demand" },
                  canView(permissionMap, "Add Purchase Order") && { label: "Purchase Request", link: "/purchase-request" },
                  canView(permissionMap, "Purchase Order List") && { label: "Purchase Order", link: "/purchase-orders" },
                  canView(permissionMap, "Delivery Note List") && { label: "Good Received", link: "/delivery-note-list" },
                  canView(permissionMap, "Add Purchase Order") && { label: "Purchase Invoice", link: "/purchase-invoice-list" },
                  canView(permissionMap, "Add Purchase Order") && { label: "Vendor Payments", link: "/vendors-payments" },
                  canView(permissionMap, "Purchase Return") && { label: "Purchase Return", link: "/purchase-return" },

                ]),
              }),
              createMenu({
                label: "Parts Sales",
                items: buildItems([
                  canView(permissionMap, "Sale Order List") && { label: "Sale Order", link: "/sale-orders" },
                  // canView(permissionMap, "Add Delivery Note") && { label: "New Delivery Note", link: "/sale-delivery-note" },
                  // canView(permissionMap, "Delivery Note List") && { label: "Delivery Note List", link: "/sale-delivery-note-list" },
                  canView(permissionMap, "Sale Return List") && { label: "Sales Return", link: "/sale-return-list" },
                  { label: "Sales Return Refunds", link: "/sales-return-refunds" },

                ]),
              }),
              // createMenu({
              //   label: "Warranty",
              //   items: buildItems([
              //     canView(permissionMap, "Add Purchase Order") && { label: "New Parts Warranty", link: "/parts-warranty" },
              //     canView(permissionMap, "Purchase Order List") && { label: "Parts Warranty List", link: "/parts-warranty-list" },
              //   ]),
              // }),

              // createMenu({
              //   label: "Reserved",
              //   items: buildItems([
              //     canView(permissionMap, "Add Purchase Order") && { label: "New Parts Reserved", link: "/parts-reserved" },
              //     canView(permissionMap, "Purchase Order List") && { label: "Parts Reserved List", link: "/parts-reserved-list" },
              //   ]),
              // }),

              // createMenu({
              //   label: "Back Order",
              //   items: buildItems([
              //     canView(permissionMap, "Add Purchase Order") && { label: "New Parts Back Order", link: "/parts-back-orders" },
              //     canView(permissionMap, "Purchase Order List") && { label: "Parts Back Order List", link: "/parts-back-orders-list" },
              //   ]),
              // }),

              // createMenu({
              //   label: "Damaged Goods",
              //   items: buildItems([
              //     canView(permissionMap, "Add Purchase Order") && { label: "New Damaged Goods", link: "/parts-damaged" },
              //     canView(permissionMap, "Purchase Order List") && { label: "Damaged Goods List", link: "/parts-damaged-list" },
              //   ]),
              // }),
              { label: "Parts Warranty", link: "/parts-warranty" },
              { label: "Parts Reserved", link: "/parts-reserved" },
              { label: "Back Order", link: "/parts-back-orders" },
              { label: "Damaged Goods", link: "/parts-damaged" },

              canView(permissionMap, "Parts Stock") && { label: "Parts Stock", link: "/parts-stock" },
              canView(permissionMap, "Parts Picking Slip") && { label: "Parts Picking Slip", link: "/parts-issue" },
              canView(permissionMap, "Parts Bin Chart") && { label: "Parts Bin Chart", link: "/parts-binning" },
              canView(permissionMap, "Parts Stock Adjustment") && { label: "Parts Stock Adjustment", link: "/PartsStockAdjustment" },
              canView(permissionMap, "Parts Stock Checking") && { label: "Parts Stock Checking", link: "/Stock-CheckAndAdj" },
            ]),
          }),
        ]),
      }),

      //   // ------------------ Inventory ------------------

      createMenu({
        label: "Finance",
        icon: <Icon.Square />,
        items: buildItems([
          // { label: "Financial", link: "/financial",},
          // { label: "Customer Ladger", link: "/customer-ledger",},
          // { label: "Company Ladger", link: "/company-ledger",},
          { label: "Chart of Accounts", link: "/finance/chart-of-accounts", },
          { label: "Journal Vouchers", link: "/finance/journal-vouchers", },
          { label: "Job Card Payments", link: "/job-card-payments", },
          { label: "Part Return Refunds", link: "/sales-return-refunds", },
          { label: "Account Ledger", link: "/finance/account-ledger", },
          { label: "Customer / Vendor Balances", link: "/finance/partner-balances", },
          { label: "Vehicle Sale Statement", link: "/finance/vehicle-sale-statement", },
          { label: "Workshop Ledger", link: "/finance/workshop-ledger", },
          { label: "Trial Balance", link: "/finance/trial-balance", },
          { label: "Profit & Loss", link: "/finance/profit-loss", },
          { label: "Balance Sheet", link: "/finance/balance-sheet", },
          { label: "Cash Flow Statement", link: "/finance/cash-flow", },
          { label: "Finance Integration", link: "/finance/integration", },
        ]),
      }),
      createMenu({
        label: "Reporting",
        icon: <Icon.PieChart />,
        items: buildItems([
          // { label: "Pre Sales Reporting", link: "/SaleReporting", },
          // { label: "Sales Reporting", link: "/SaleSummary", },
          // Grouped by module (same reports as before)
          createMenu({
            label: "Sales",
            items: buildItems([
              canView(permissionMap, "Dealers Sales Journey") && { label: "Dealers Sales Journey", link: "/sales-wise-report", },
              canView(permissionMap, "Pre-Sales Performance") && { label: "Pre-Sales Performance", link: "/presales-performance-report" },
              canView(permissionMap, "Province Wise Sales") && { label: "Province Wise Sales", link: "/province-wise-report", },
              canView(permissionMap, "Vehicle Sales Payments") && { label: "Payments OE", link: "/payments-oe", },
            ]),
          }),
          createMenu({
            label: "CRM",
            items: buildItems([
              canView(permissionMap, "CRM Revenue") && { label: "CRM Revenue Report", link: "/crm-revenue-report", },
            ]),
          }),
          createMenu({
            label: "After Sales",
            items: buildItems([
              canView(permissionMap, "DQIR") && { label: "DQIR", link: "/dqir-list", },
              canView(permissionMap, "DQIR Revenue") && { label: "DQIR Revenue", link: "/dqir-revenue", },
              canView(permissionMap, "Job Card Completion") && { label: "Job Card Completion", link: "/job-card-completion-report", },
              canView(permissionMap, "Technician Efficiency") && { label: "Workshop Load / Technician Efficiency", link: "/technician-efficiency-report", },
              canView(permissionMap, "Technician Efficiency") && { label: "Technician Job Breakdown", link: "/technician-job-breakdown-report", },
              canView(permissionMap, "Daily Job Card Counter") && { label: "Daily Job Card Counter", link: "/daily-jobcard-counter-report", },
              canView(permissionMap, "Appointments vs Visitors") && { label: "Appointments vs Visitors", link: "/appointments-vs-visitors-report", },
              canView(permissionMap, "Workshop Revenue") && { label: "Workshop Revenue", link: "/workshop-revenue-report", },
            ]),
          }),
          createMenu({
            label: "Parts",
            items: buildItems([
              canView(permissionMap, "Parts Low Stock") && { label: "Parts Low Stock", link: "/low-stock-report", },
              canView(permissionMap, "Vehicle Wise Parts Sales Ledger") && { label: "Vehicle Wise Parts Sales ledger", link: "/Vehicle-Wise-Parts-Sales-ledger", },
              canView(permissionMap, "Customer Wise Sales Ledger") && { label: "Customer Wise Sales ledger", link: "/Customer-wise-sales-ledger", },
              canView(permissionMap, "Daily Sales Ledger") && { label: "Daily sales ledger", link: "/Daily-sales-ledger", },
              canView(permissionMap, "Item Wise Sales Ledger") && { label: "Item Wise Sales Ledger", link: "/Item-Wise-Sales-Ledger", },
              canView(permissionMap, "Fast Moving Items") && { label: "Fast Moving Items", link: "/Fast-moving-items", },
              canView(permissionMap, "Items In Demand Ledger") && { label: "Items In Demand ledger", link: "/Items-In-demand-ledger", },
              canView(permissionMap, "Parts Counter Sale") && { label: "Parts Counter Sale", link: "/parts-counter-sale-report", },
              canView(permissionMap, "Parts Workshop Sale") && { label: "Parts Workshop Sale", link: "/parts-workshop-sale-report", },
              canView(permissionMap, "Parts: Target vs Invoice") && { label: "Parts: Target vs Invoice", link: "/parts-target-vs-invoice-report", },
            ]),
          }),
        ]),
      }),
      // ------------------ HR & Payroll ------------------
      createMenu({
        label: "HR & Payroll",
        icon: <Icon.Briefcase />,
        items: buildItems([
          hrView("HR Employees") && { label: "Employees", link: "/hr-employees" },
          createMenu({
            label: "Attendance",
            items: buildItems([
              hrView("HR Attendance") && { label: "Daily Attendance", link: "/hr-daily-attendance" },
              hrView("HR Attendance") && { label: "Attendance Import", link: "/hr-attendance-import" },
              hrView("HR Attendance") && { label: "Monthly Sheet", link: "/hr-monthly-attendance" },
            ]),
          }),
          createMenu({
            label: "Leave",
            items: buildItems([
              hrView("HR Leave") && { label: "Leave Requests", link: "/hr-leave-requests" },
              hrView("HR Leave") && { label: "Leave Balances", link: "/hr-leave-balances" },
            ]),
          }),
          createMenu({
            label: "Payroll",
            items: buildItems([
              hrView("HR Salary Structure") && { label: "Salary Structure", link: "/hr-salary-structure" },
              hrView("HR Loans") && { label: "Loans & Advances", link: "/hr-loans" },
              hrView("HR Incentives") && { label: "Incentives", link: "/hr-incentives" },
              hrView("HR Payroll Run") && { label: "Payroll Runs", link: "/hr-payroll-runs" },
              hrView("HR Payslips") && { label: "Payslips", link: "/hr-payslips" },
              hrView("HR Final Settlement") && { label: "Final Settlement", link: "/hr-final-settlement" },
            ]),
          }),
          hrView("HR Reports") && { label: "HR Reports", link: "/hr-reports" },
          createMenu({
            label: "HR Setup",
            items: buildItems([
              hrView("HR Setup") && { label: "Designations", link: "/hr-designations" },
              hrView("HR Setup") && { label: "Grades", link: "/hr-grades" },
              hrView("HR Setup") && { label: "Leave Types", link: "/hr-leave-types" },
              hrView("HR Setup") && { label: "Pay Components", link: "/hr-pay-components" },
              hrView("HR Setup") && { label: "Tax Slabs", link: "/hr-tax-slabs" },
              hrView("HR Setup") && { label: "Payroll Settings", link: "/hr-payroll-settings" },
            ]),
          }),
        ]),
      }),
      createMenu({
        label: "Setup (OEM)",
        icon: <Icon.Settings />,
        items: buildItems([
          canView(permissionMap, "Quota Request") && { label: "Quota Request", icon: <Icon.Box />, link: "/quota-request" },
          createMenu({
            label: "Assign Target",
            icon: <Icon.Award />,
            items: buildItems([
              // { label: "Distributor Qouta", link: "/distributor-qouta" },
              // { label: "Dealer Qouta", link: "/dealer-qouta" },
              canView(permissionMap, "Salesman Target") && { label: "Salesman Target", link: "/salesman-qouta" },
            ]),
          }),
          createMenu({
            label: "Deliveries (OEM)",
            icon: <Icon.Folder />,
            items: buildItems([
              canView(permissionMap, "Vehicle Stock") && { label: "Vehicle Stock", link: "/stock" },
              canView(permissionMap, "Stock Movement") && { label: "Stock Movement", link: "/StockMovement" },
            ]),
          }),
          createMenu({
            label: "Parts",
            items: buildItems([
              canView(permissionMap, "Parts") && { label: "Parts", link: "/parts" },
              canView(permissionMap, "Parts") && { label: "Make", link: "/make" },
              canView(permissionMap, "Parts") && { label: "Model", link: "/model" },
              canView(permissionMap, "Parts") && { label: "Variant", link: "/variant" },
              canView(permissionMap, "Parts") && { label: "Category", link: "/category" },
              canView(permissionMap, "Parts") && { label: "Colors", link: "/part-color" },
            ]),
          }),
          createMenu({
            label: "Vehicle",
            items: buildItems([
              canView(permissionMap, "Vehicle") && { label: "Vehicle", link: "/vehicle" },
              { label: "Vehicle Variant", link: "/vehicle-variant" },
            ]),
          }),
          createMenu({
            label: "Auth",
            items: buildItems([
              canView(permissionMap, "Auth") && { label: "Assign Roles", link: "/roles" },
              canView(permissionMap, "Auth") && { label: "Assign Roles Setup", link: "/auth-setup" },
              canView(permissionMap, "Auth") && { label: "Users", link: "/user-setup" },
              canView(permissionMap, "Auth") && { label: "Roles", link: "/roles-setup" },
              canView(permissionMap, "Auth") && { label: "Modules", link: "/module" },
              canView(permissionMap, "Auth") && { label: "Screens", link: "/screen" },
              canView(permissionMap, "Auth") && { label: "Sub Screens", link: "/sub-screen" },
              canView(permissionMap, "Auth") && { label: "Privileges", link: "/privileges" },
            ]),
          }),
          // createMenu({
          //   label: "Finance Setup",
          //   icon: <Icon.Square />,
          //   items: buildItems([
            //     canView(permissionMap, "Finance Setup") && { label: "COA Config",    link: "/coa-config",},
            //     canView(permissionMap, "Finance Setup") && { label: "Code Prefix Config", link: "/code-config",},
            //   ]),
            // }),
            employeeView("Year Calender") && { label: "Year Calender", link: "/year-calender" },
            employeeView("Technician") && { label: "Technician", link: "/technician" },
            employeeView("Holidays") && { label: "Holidays", link: "/holidays" },
            employeeView("Teams") && { label: "Teams", link: "/teams" },
            employeeView("Shift") && { label: "Shift", link: "/shifts" },
            employeeView("Types") && { label: "Types", link: "/types" },
            employeeView("Skills") && { label: "Skills", link: "/skills" },
            canView(permissionMap, "Customers") && { label: "Customers", link: "/customers", icon: <Icon.Users />, showSubRoute: false, submenu: false, },
            canView(permissionMap, "Bank") && { label: "Bank", link: "/bank" },
            canView(permissionMap, "Color") && { label: "Color", link: "/color" },
            canView(permissionMap, "City") && { label: "City", link: "/city" },
          canView(permissionMap, "Dealer") && { label: "Dealer", link: "/dealer" },
          canView(permissionMap, "Company") && { label: "Company", link: "/company" },
          canView(permissionMap, "Source") && { label: "Source", link: "/source" },
          canView(permissionMap, "Vehicle") && { label: "Vehicle", link: "/vehicle" },
          canView(permissionMap, "Quota") && { label: "Quota", link: "/quota" },
          canView(permissionMap, "Corporate Clients") && { label: "Corporate Clients", link: "/corporate-clients" },
          canView(permissionMap, "Job Categories") && { label: "Job Categories", link: "/job-categories" },
          { label: "Insurance Companies", link: "/insurance-companies" },
          canView(permissionMap, "Taxes") && { label: "Taxes", link: "/taxes" },
          canView(permissionMap, "Vendors") && { label: "Vendors", link: "/vendors" },
          canView(permissionMap, "Transporter") && { label: "Transporter", link: "/transporter" },
          { label: "Code Prefix Config", link: "/code-config", },
          { label: "Departments", link: "/department", }
        ]),
      }),
      createMenu({
        label: "Discount",
        icon: <Icon.Tag />,
        items: buildItems([
          { label: "Add Campaign", link: "/discount", },
          { label: "Campaign List", link: "/discount-list", },
        ]),
      }),
    ]),

  }
];

// ------------------ DISTRIBUTOR MENU ------------------
// Arranged by the distributor's work: approvals and vehicle supply first, dealer screens
// (view only) grouped lower. Same items and the same permission check on each as the dealer
// menu — only their place changes. Customer Journey stays in the header search.
const DistributorSidebarData = [
  {
    submenuHdr: "Main",
    submenuOpen: true,
    submenuItems: buildItems([
      // Single link — "/" is the distributor's home dashboard
      { label: "Dashboard", icon: <Icon.Grid />, link: "/", submenu: false, showSubRoute: false },

      // Distributor's daily work, from every module
      createMenu({
        label: "Approvals",
        icon: <Icon.CheckSquare />,
        items: buildItems([
          canView(permissionMap, "Vehicle Sales Payments") && { label: "Vehicle Sales Payments", link: "/payments-oe" },
          canView(permissionMap, "Cancel/Change Sale Request") && { label: "Cancel or Change Sale", link: "/cancleandchangesaleRequest" },
          canView(permissionMap, "Quota Request") && { label: "Quota Request", link: "/quota-request" },
          canView(permissionMap, "DQIR") && { label: "DQIR Review", link: "/dqir-list" },
          { label: "Parts Warranty", link: "/parts-warranty" },
          canView(permissionMap, "CRM Complain") && { label: "Complaint", link: "/CrmComplaint" },
        ]),
      }),

      createMenu({
        label: "Sales Orders",
        icon: <Icon.FileText />,
        items: buildItems([
          canView(permissionMap, "Order List") && { label: "Order List", link: "/sales-inquiries" },
          canView(permissionMap, "Add Sales") && { label: "New Sales", link: "/sales-add" },
          canView(permissionMap, "Payments") && { label: "Payments", link: "/payments" },
          { label: "Deposit Slips", link: "/deposit-slips" },
          canView(permissionMap, "Cancelled Sale") && { label: "Cancelled Sale", link: "/Cancelled-Sales" },
        ]),
      }),

      createMenu({
        label: "Vehicle Supply",
        icon: <Icon.Truck />,
        items: buildItems([
          canView(permissionMap, "Vehicle Stock") && { label: "Vehicle Stock", link: "/stock" },
          canView(permissionMap, "Stock Movement") && { label: "Stock Movement", link: "/StockMovement" },
        ]),
      }),

      createMenu({
        label: "Targets",
        icon: <Icon.Award />,
        items: buildItems([
          canView(permissionMap, "Salesman Target") && { label: "Salesman Target", link: "/salesman-qouta" },
        ]),
      }),

      createMenu({
        label: "Reports",
        icon: <Icon.PieChart />,
        items: buildItems([
          createMenu({
            label: "Sales",
            items: buildItems([
              canView(permissionMap, "Dealers Sales Journey") && { label: "Dealers Sales Journey", link: "/sales-wise-report" },
              canView(permissionMap, "Pre-Sales Performance") && { label: "Pre-Sales Performance", link: "/presales-performance-report" },
              canView(permissionMap, "Province Wise Sales") && { label: "Province Wise Sales", link: "/province-wise-report" },
              canView(permissionMap, "Dealer-wise Vehicle Sales: Target vs Actual") && { label: "Dealer-wise Vehicle Sales: Target vs Actual", link: "/dealer-vehicle-target-report" },
              canView(permissionMap, "Model Wise Booking Summary") && { label: "Model Wise Booking Summary", link: "/model-wise-booking-report" },
            ]),
          }),
          createMenu({
            label: "CRM",
            items: buildItems([
              canView(permissionMap, "CRM Revenue") && { label: "CRM Revenue Report", link: "/crm-revenue-report" },
              canView(permissionMap, "Customer Complaints") && { label: "Customer Complaints", link: "/customer-complaints-report" },
            ]),
          }),
          createMenu({
            label: "After Sales",
            items: buildItems([
              canView(permissionMap, "DQIR Revenue") && { label: "DQIR Revenue", link: "/dqir-revenue" },
              canView(permissionMap, "Job Card Completion") && { label: "Job Card Completion", link: "/job-card-completion-report" },
              canView(permissionMap, "Technician Efficiency") && { label: "Workshop Load / Technician Efficiency", link: "/technician-efficiency-report" },
              canView(permissionMap, "Technician Efficiency") && { label: "Technician Job Breakdown", link: "/technician-job-breakdown-report" },
              canView(permissionMap, "Daily Job Card Counter") && { label: "Daily Job Card Counter", link: "/daily-jobcard-counter-report" },
              canView(permissionMap, "Appointments vs Visitors") && { label: "Appointments vs Visitors", link: "/appointments-vs-visitors-report" },
              canView(permissionMap, "Job Cards: Cash vs Credit") && { label: "Job Cards: Cash vs Credit", link: "/jobcards-cash-vs-credit-report" },
            ]),
          }),
          createMenu({
            label: "Parts",
            items: buildItems([
              canView(permissionMap, "Parts Low Stock") && { label: "Parts Low Stock", link: "/low-stock-report" },
              canView(permissionMap, "Vehicle Wise Parts Sales Ledger") && { label: "Vehicle Wise Parts Sales ledger", link: "/Vehicle-Wise-Parts-Sales-ledger" },
              canView(permissionMap, "Customer Wise Sales Ledger") && { label: "Customer Wise Sales ledger", link: "/Customer-wise-sales-ledger" },
              canView(permissionMap, "Daily Sales Ledger") && { label: "Daily sales ledger", link: "/Daily-sales-ledger" },
              canView(permissionMap, "Item Wise Sales Ledger") && { label: "Item Wise Sales Ledger", link: "/Item-Wise-Sales-Ledger" },
              canView(permissionMap, "Fast Moving Items") && { label: "Fast Moving Items", link: "/Fast-moving-items" },
              canView(permissionMap, "Items In Demand Ledger") && { label: "Items In Demand ledger", link: "/Items-In-demand-ledger" },
              canView(permissionMap, "Parts Counter Sale") && { label: "Parts Counter Sale", link: "/parts-counter-sale-report" },
              canView(permissionMap, "Parts Workshop Sale") && { label: "Parts Workshop Sale", link: "/parts-workshop-sale-report" },
              canView(permissionMap, "Parts: Target vs Invoice") && { label: "Parts: Target vs Invoice", link: "/parts-target-vs-invoice-report" },
              canView(permissionMap, "Parts Requests") && { label: "Parts Requests", link: "/parts-requests-report" },
            ]),
          }),
        ]),
      }),

      createMenu({
        label: "Finance",
        icon: <Icon.Square />,
        items: buildItems([
          { label: "Chart of Accounts", link: "/finance/chart-of-accounts" },
          { label: "Journal Vouchers", link: "/finance/journal-vouchers" },
          { label: "Job Card Payments", link: "/job-card-payments" },
          { label: "Part Return Refunds", link: "/sales-return-refunds" },
          { label: "Account Ledger", link: "/finance/account-ledger" },
          { label: "Customer / Vendor Balances", link: "/finance/partner-balances" },
          { label: "Vehicle Sale Statement", link: "/finance/vehicle-sale-statement" },
          { label: "Workshop Ledger", link: "/finance/workshop-ledger" },
          { label: "Trial Balance", link: "/finance/trial-balance" },
          { label: "Profit & Loss", link: "/finance/profit-loss" },
          { label: "Balance Sheet", link: "/finance/balance-sheet" },
          { label: "Cash Flow Statement", link: "/finance/cash-flow" },
          { label: "Finance Integration", link: "/finance/integration" },
        ]),
      }),

      // Dealer screens — the distributor only views these
      createMenu({
        label: "Dealer Operations",
        icon: <Icon.Eye />,
        items: buildItems([
          createMenu({
            label: "CRM",
            items: buildItems([
              canView(permissionMap, "CRM Inquiry") && { label: "Inquiry", link: "/CrmInquiry" },
              canView(permissionMap, "Appointment Due List") && { label: "Service Due List", link: "/mrs-reminders" },
              canView(permissionMap, "Add Appointment") && { label: "New Appointment", link: "/add-appointment" },
              canView(permissionMap, "Confirm Appointments") && { label: "Confirm Appointments", link: "/crm-appointments" },
              { label: "Appointment Inquiries", link: "/AppointmentReconfirmation" },
            ]),
          }),
          createMenu({
            label: "Pre Sales",
            items: buildItems([
              canView(permissionMap, "Customer Inquiry") && { label: "Customer Inquiry", link: "/visiting" },
              canView(permissionMap, "Prospect") && { label: "Prospect", link: "/lead" },
              canView(permissionMap, "Test Drive") && { label: "Test Drive", link: "/test-drive" },
            ]),
          }),
          createMenu({
            label: "After Sales",
            items: buildItems([
              canView(permissionMap, "Add Estimation") && { label: "New Estimation/Jobcard", link: "/JobCard" },
              canView(permissionMap, "Estimation List") && { label: "Estimations List", link: "/job-card-estimations" },
              canView(permissionMap, "Workshop Load") && { label: "Workshop Load", link: "/workshop-view" },
              canView(permissionMap, "Open Inspection Job Card") && { label: "Open Inspection", link: "/job-card-inspections" },
              canView(permissionMap, "Open Job Card") && { label: "Open Job Card", link: "/job-card-inquiries" },
              canView(permissionMap, "Post Service Follow-Up") && { label: "Post Service Follow-Up", link: "/PostServiceFollowUp" },
              canView(permissionMap, "PDS Requests") && { label: "PDS Requests", link: "/pds-requests" },
            ]),
          }),
          createMenu({
            label: "Parts",
            items: buildItems([
              canView(permissionMap, "Add Purchase Order") && { label: "Parts Demand", link: "/parts-demand" },
              canView(permissionMap, "Add Purchase Order") && { label: "Purchase Request", link: "/purchase-request" },
              canView(permissionMap, "Purchase Order List") && { label: "Purchase Order", link: "/purchase-orders" },
              canView(permissionMap, "Delivery Note List") && { label: "Good Received", link: "/delivery-note-list" },
              canView(permissionMap, "Add Purchase Order") && { label: "Purchase Invoice", link: "/purchase-invoice-list" },
              canView(permissionMap, "Add Purchase Order") && { label: "Vendor Payments", link: "/vendors-payments" },
              canView(permissionMap, "Purchase Return") && { label: "Purchase Return", link: "/purchase-return" },
              canView(permissionMap, "Sale Order List") && { label: "Sale Order", link: "/sale-orders" },
              canView(permissionMap, "Sale Return List") && { label: "Sales Return", link: "/sale-return-list" },
              { label: "Parts Reserved", link: "/parts-reserved" },
              { label: "Back Order", link: "/parts-back-orders" },
              { label: "Damaged Goods", link: "/parts-damaged" },
              canView(permissionMap, "Parts Stock") && { label: "Parts Stock", link: "/parts-stock" },
              canView(permissionMap, "Parts Picking Slip") && { label: "Parts Picking Slip", link: "/parts-issue" },
              canView(permissionMap, "Parts Bin Chart") && { label: "Parts Bin Chart", link: "/parts-binning" },
              canView(permissionMap, "Parts Stock Adjustment") && { label: "Parts Stock Adjustment", link: "/PartsStockAdjustment" },
              canView(permissionMap, "Parts Stock Checking") && { label: "Parts Stock Checking", link: "/Stock-CheckAndAdj" },
            ]),
          }),
        ]),
      }),

      createMenu({
        label: "Setup",
        icon: <Icon.Settings />,
        items: buildItems([
          createMenu({
            label: "Masters",
            items: buildItems([
              canView(permissionMap, "Vehicle") && { label: "Vehicle", link: "/vehicle" },
              { label: "Vehicle Variant", link: "/vehicle-variant" },
              canView(permissionMap, "Dealer") && { label: "Dealer", link: "/dealer" },
              canView(permissionMap, "City") && { label: "City", link: "/city" },
              canView(permissionMap, "Color") && { label: "Color", link: "/color" },
              canView(permissionMap, "Bank") && { label: "Bank", link: "/bank" },
              canView(permissionMap, "Company") && { label: "Company", link: "/company" },
              canView(permissionMap, "Source") && { label: "Source", link: "/source" },
              canView(permissionMap, "Corporate Clients") && { label: "Corporate Clients", link: "/corporate-clients" },
              canView(permissionMap, "Job Categories") && { label: "Job Categories", link: "/job-categories" },
              { label: "Insurance Companies", link: "/insurance-companies" },
              canView(permissionMap, "Taxes") && { label: "Taxes", link: "/taxes" },
              canView(permissionMap, "Vendors") && { label: "Vendors", link: "/vendors" },
              canView(permissionMap, "Transporter") && { label: "Transporter", link: "/transporter" },
              canView(permissionMap, "CRM Complain") && { label: "Complaint Setup", link: "/CrmComplaintSetup" },
              { label: "Departments", link: "/department" },
              canView(permissionMap, "Quota") && { label: "Quota", link: "/quota" },
              { label: "Code Prefix Config", link: "/code-config" },
            ]),
          }),
          createMenu({
            label: "Parts Masters",
            items: buildItems([
              canView(permissionMap, "Parts") && { label: "Parts", link: "/parts" },
              canView(permissionMap, "Parts") && { label: "Make", link: "/make" },
              canView(permissionMap, "Parts") && { label: "Model", link: "/model" },
              canView(permissionMap, "Parts") && { label: "Variant", link: "/variant" },
              canView(permissionMap, "Parts") && { label: "Category", link: "/category" },
              canView(permissionMap, "Parts") && { label: "Colors", link: "/part-color" },
            ]),
          }),
          createMenu({
            label: "Employee",
            items: buildItems([
              employeeView("Employee") && { label: "Year Calender", link: "/year-calender" },
              employeeView("Employee") && { label: "Technician", link: "/technician" },
              employeeView("Employee") && { label: "Holidays", link: "/holidays" },
              employeeView("Employee") && { label: "Teams", link: "/teams" },
              employeeView("Employee") && { label: "Shift", link: "/shifts" },
              employeeView("Employee") && { label: "Types", link: "/types" },
              employeeView("Employee") && { label: "Skills", link: "/skills" },
            ]),
          }),
          createMenu({
            label: "People",
            items: buildItems([
              canView(permissionMap, "Customers") && { label: "Customers", link: "/customers" },
              canView(permissionMap, "Customers") && { label: "Employees", link: "/employees" },
            ]),
          }),
          createMenu({
            label: "Auth",
            items: buildItems([
              canView(permissionMap, "Auth") && { label: "Assign Roles", link: "/roles" },
              canView(permissionMap, "Auth") && { label: "Assign Roles Setup", link: "/auth-setup" },
              canView(permissionMap, "Auth") && { label: "Users", link: "/user-setup" },
              canView(permissionMap, "Auth") && { label: "Roles", link: "/roles-setup" },
              canView(permissionMap, "Auth") && { label: "Modules", link: "/module" },
              canView(permissionMap, "Auth") && { label: "Screens", link: "/screen" },
              canView(permissionMap, "Auth") && { label: "Sub Screens", link: "/sub-screen" },
              canView(permissionMap, "Auth") && { label: "Privileges", link: "/privileges" },
            ]),
          }),
        ]),
      }),
    ]),
  },
];

// Dealer-level login (dealer in the token) → dealer menu; otherwise distributor menu
const SidebarData = loginUser.dealerId ? DealerSidebarData : DistributorSidebarData;

export default SidebarData;
